<template>
  <idle-display :duration="60 * idle_timeout">
    <div
      id="app"
      class="h-100"
      :class="[skinClasses]"
    >
      <component :is="layout" v-cloak>
        <router-view :key="$route.path" />
      </component>

      <scroll-to-top v-if="enableScrollToTop" />

      <div id="sound-source" style="display: none"></div>
    </div>

    <video-modal />
    <outgoing-call-modal />
    <camera-permission-modal />
  </idle-display>
</template>

<script>
import VideoModal from "@core/components/video-modal/video-modal.vue"
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import OutgoingCallModal from "@core/components/video-modal/outgoing-call-modal.vue"
import CameraPermissionModal from "@core/components/video-modal/camera-permission-modal.vue"

import { get } from "lodash";
import { watch } from '@vue/composition-api';
import { useCssVar, useWindowSize } from '@vueuse/core';
import { provideToast } from 'vue-toastification/composition';
import { MUTATE_ONLINE_USERS } from "@/store/config/mutation-keys";
import { $themeBreakpoints, $themeColors, $themeConfig } from '@themeConfig';

import store from '@/store';
import WebRTC from "@/@core/mixins/webrtc";
import useAppConfig from '@core/app-config/useAppConfig';
import IdleDisplay from "@core/components/shared/idle/IdleDisplay.vue";

const LayoutFull = () => import('@/layouts/full/LayoutFull.vue');
const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue');

export default {
  components: {
    VideoModal,
    IdleDisplay,
    OutgoingCallModal,
    CameraPermissionModal,

    // Layouts
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  mixins: [WebRTC],
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  data(){
    return {
      loaded: false,
      loading: false,
      heartBeatIntervalId: null,
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    settings() {
      return this.$store.getters[`auth/settings`];
    },
    idle_timeout() {
      return get(this.settings, 'security_settings.idle_timeout', 5)
    }
  },
  watch: {
    isLoggedIn: {
      handler(state){
        if (state && this.$socket.client.disconnected){
          this.initializeSocketIO();
        }
      },
      immediate: true
    },
    "$socket.disconnected": {
      handler(disconnected){
        disconnected && console.log('SOCKET DISCONNECTED', disconnected)
        // if (disconnected){
        //   this.onClearHeartbeatService()
        // }

        if (disconnected && this.isLoggedIn){
          this.retrySocketConnectionWithExponentialBackoff();
        }
      }, 
      deep: true,
      immediate: true
    },
    "$socket.connected": {
      handler(connected){
        connected && console.log("[SOCKET CONNECTED FROM WATCHER]")
        // connected && this.onInitializeHeartbeats()
      }, 
      deep: true,
      immediate: true
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }


    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  beforeDestroy() {
    if (this.heartBeatIntervalId){
      clearInterval(this.heartBeatIntervalId)
    }
  },
  destroyed() {
    this.$socket.client.close()
  },
  methods: {
    updateOnlineUsers(onlineUsers){
      this.$store.commit(`auth/${MUTATE_ONLINE_USERS}`, onlineUsers);
    },
    onInitializeHeartbeats() {
      clearInterval(this.heartBeatIntervalId);

      this.heartBeatIntervalId = setInterval(() => {
        console.log('SEND HEARTBEAT')
        this.$socket.client.emit('heartbeat');
      }, 5e3);

      console.log('HEARTBEAT INITIALIZED', this.heartBeatIntervalId)
    },
    onClearHeartbeatService() {
      if (this.heartBeatIntervalId){
        clearInterval(this.heartBeatIntervalId);
        console.log('HEARTBEAT DE-INITIALIZED',)
      }
    }
  },
}
</script>

<style scoped>
  [v-cloak] {
    display: none;
  }

  .vm--container {
    pointer-events: none !important;
    background: transparent !important;
  }
  .vm--overlay {
    width: 0 !important;
    height: 0 !important
  }
</style>
